import routeConfig from "../../configs/route.config"
import menuPrivilegeStore from "../../stores/menu-privilege.store"
import { useTranslation } from "react-i18next"
import { transparentize } from "@chakra-ui/theme-tools"
import { GroupMenuProps, MenuProps, StandaloneMenuProps } from "../../types/sidebar-v2.type"
import { useColorMode, useTheme } from "@chakra-ui/react"
import teamStore from "../../stores/team.store"

export default function useSidebarV2() {
  const { t } = useTranslation()
  const { colorMode } = useColorMode()
  const { team } = teamStore()
  const menuPrivilegeState = menuPrivilegeStore()
  const theme = useTheme()
  
  const themeCode = team?.theme_object?.code
  const defaultGradient = 'linear-gradient(180deg, #0079D2 28.92%, #0086E8 38.93%, #158EE7 50.86%, #1495F4 64.31%, #1096F8 78.29%, #1699FA 89.73%, #169AFB 100%)'
  const backgroundMap: Record<string, string> = {
    Blue: defaultGradient,
    'Grey-vis': '#f0f4fb',
    'Green-vis': '#96c45e',
    'Yellow-vis': '#f1b801',
    'Red-vis': '#e03d3d',
    'Purple-vis': '#6141BC',
    'Dark Green-vis': '#0C6B2C',
  }

  const searchBarBackgroundColor = transparentize("onSurface", 0.12)(theme)
  const textColor = colorMode === 'light'
      ? themeCode === 'Grey-vis'
        ? 'onBackground'
        : 'onPrimary'
      : 'onSurface'
  const backgroundColor = colorMode === 'light'
      ? themeCode
        ? backgroundMap[themeCode] ?? defaultGradient
        : defaultGradient
      : 'surfacePlus1'

  const betaMenu: StandaloneMenuProps = {
    type: "standalone",
    label: t('SidebarV2.Menu.TryBetaVersion'),
    icon: 'rocket_launch',
    href: 'https://beta.jarivis.com',
    isExternal: true,
    isNew: true,
  }

  const groupMenus: GroupMenuProps[] = [
    {
      label: t('SidebarV2.GroupMenu.Dashboard'),
      menus: [
        { type: "standalone", label: t('SidebarV2.Menu.AddOnDashboard'), icon: 'dashboard', href: routeConfig.dashboardPersonalAddOn, privilege: 1009, teamIds: [714] },
        { type: "standalone", label: t('SidebarV2.Menu.PersonalDashboard'), icon: 'dashboard', href: routeConfig.dashboardPersonal, privilege: 1009, teamIds: [714, 715, 609, 1181] },
        { type: "standalone", label: t('SidebarV2.Menu.Dashboard'), icon: 'home', href: routeConfig.dashboard, privilege: 1009 },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Customer'),
      menus: [
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.CustomerMaster'), 
          icon: 'support_agent',  
          rootHref: routeConfig.customer.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.CustomerMaster'), href: routeConfig.customer.master, privilege: 1008 },
            { label: t('SidebarV2.SubMenu.CustomerSetting'), href: routeConfig.customer.setting, privilege: 1008 },
            { label: t('SidebarV2.SubMenu.CustomerAnalysis'), href: routeConfig.customer.analysis, privilege: 1008 },
          ]
        },
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.LeadsMaster'), 
          icon: 'dataset',  
          rootHref: routeConfig.leads.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.LeadsMaster'), href: routeConfig.leads.master, privilege: 1032 },
            { label: t('SidebarV2.SubMenu.LeadsDashboard'), href: routeConfig.leads.dashboard, privilege: 1032 },
            { label: t('SidebarV2.SubMenu.LeadsActivity'), href: routeConfig.leads.activity, privilege: 1032 },
            { label: t('SidebarV2.SubMenu.LeadsSetting'), href: routeConfig.leads.setting, privilege: 1032 },
          ]
        },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Task'),
      menus: [
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.TaskMaster'), 
          icon: 'task',  
          rootHref: routeConfig.task.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.TaskMaster'), href: routeConfig.task.master, privilege: 1013 },
            { label: t('SidebarV2.SubMenu.TaskType'), href: routeConfig.task.type, privilege: 1003 },
            { label: t('SidebarV2.SubMenu.TaskMonitor'), href: routeConfig.task.monitoring, privilege: 1020 },
            { label: t('SidebarV2.SubMenu.TaskSetting'), href: routeConfig.task.setting },
            { label: t('SidebarV2.SubMenu.TaskAnalysis'), href: routeConfig.task.analysis },
          ]
        },
        { type: "standalone", label: t('SidebarV2.Menu.TaskCalendarView'), icon: 'calendar_today', href: routeConfig.task.calendar, isNew: true },
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.TicketManagement'), 
          icon: 'confirmation_number',  
          rootHref: routeConfig.ticket.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.TicketMaster'), href: routeConfig.ticket.master, privilege: 1027 },
            { label: t('SidebarV2.SubMenu.TicketSetting'), href: routeConfig.ticket.setting, privilege: 1027 },
          ]
        },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Member'),
      menus: [
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.Member'), 
          icon: 'groups',  
          rootHref: routeConfig.member.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.MemberMaster'), href: routeConfig.member.master, privilege: 1017 },
            { label: t('SidebarV2.SubMenu.MemberDetail'), href: routeConfig.member.detail  },
            { label: t('SidebarV2.SubMenu.MemberGroup'), href: routeConfig.member.group },
            { label: t('SidebarV2.SubMenu.MemberList'), href: routeConfig.member.list, privilege: 1018 },
            { label: t('SidebarV2.SubMenu.MemberSetting'), href: routeConfig.member.setting },
            { label: t('SidebarV2.SubMenu.MemberFeedback'), href: routeConfig.member.feedback, privilege: 1024 },
          ]
        },
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.Leave'), 
          icon: 'event_available',  
          rootHref: routeConfig.leave.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.LeaveSetting'), href: routeConfig.leave.setting, privilege: 1025 },
            { label: t('SidebarV2.SubMenu.LeaveApproval'), href: routeConfig.leave.approval, privilege: 1026 },
          ]
        },
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.Attendance'), 
          icon: 'co_present',  
          rootHref: routeConfig.attendance.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.AttendanceMonitoring'), href: routeConfig.attendance.monitoring, privilege: 1021 },
            { label: t('SidebarV2.SubMenu.AttendanceSetting'), href: routeConfig.attendance.setting, privilege: 1022 },
          ]
        },
        { type: "standalone", label: t('SidebarV2.Menu.CostPerdin'), icon: 'attach_money', href: routeConfig.costPerdin.root, privilege: 1033 },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Tracking'),
      menus: [
        { type: "standalone", label: t('SidebarV2.Menu.TrackingGeofence'), icon: 'diversity_3', href: routeConfig.tracking.geofence, privilege: 1037 },
        { type: "standalone", label: t('SidebarV2.Menu.TrackingLocation'), icon: 'location_on', href: routeConfig.tracking.location },
        { type: "standalone", label: t('SidebarV2.Menu.TrackingJourney'), icon: 'map', href: routeConfig.tracking.journey },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Pos'),
      menus: [
        { type: "standalone", label: t('SidebarV2.Menu.MitraMaster'), icon: 'handshake', href: routeConfig.pos.masterMitra, privilege: 1028 },
        { type: "standalone", label: t('SidebarV2.Menu.OutletMaster'), icon: 'store', href: routeConfig.pos.masterOutlet, privilege: 1029 },
        { type: "standalone", label: t('SidebarV2.Menu.ProductMaster'), icon: 'category', href: routeConfig.pos.masterProduct, privilege: 1030 },
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.Pos'), 
          icon: 'point_of_sale',  
          rootHref: routeConfig.pos.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.PosDashboard'), href: routeConfig.pos.dashboard, privilege: 1031 },
            { label: t('SidebarV2.SubMenu.PosCommission'), href: routeConfig.pos.commission, privilege: 1031 },
            { label: t('SidebarV2.SubMenu.PosTransaction'), href: routeConfig.pos.transaction, privilege: 1031 },
            { label: t('SidebarV2.SubMenu.PosSettlement'), href: routeConfig.pos.settlement, privilege: 1031 },
          ]
        },

      ]
    },
    {
      label: t('SidebarV2.GroupMenu.SalesOrder'),
      menus: [
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.SalesOrder'), 
          icon: 'receipt_long',  
          rootHref: routeConfig.salesOrder.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.SalesOrderProduct'), href: routeConfig.salesOrder.product, privilege: 1035 },
            { label: t('SidebarV2.SubMenu.SalesOrderTransaction'), href: routeConfig.salesOrder.transaction, privilege: 1035 },
            { label: t('SidebarV2.SubMenu.SalesOrderSetting'), href: routeConfig.salesOrder.setting, privilege: 1035 },
          ]
        },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Team'),
      menus: [
        { type: "standalone", label: t('SidebarV2.Menu.Team'), icon: 'workspaces', href: routeConfig.team.team },
        { type: "standalone", label: t('SidebarV2.Menu.TeamBulletin'), icon: 'assignment_turned_in', href: routeConfig.bulletin.master },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Report'),
      menus: [
        { type: "standalone", label: t('SidebarV2.Menu.Report'), icon: 'report', href: routeConfig.report, privilege: 1001 },
      ]
    },
    {
      label: t('SidebarV2.GroupMenu.Other'),
      menus: [
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.Configuration'), 
          icon: 'tune',  
          rootHref: routeConfig.configuration.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.ConfigurationLookup'), href: routeConfig.configuration.lookupList },
          ]
        },
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.Builder'), 
          icon: 'construction',  
          rootHref: routeConfig.builder.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.BuilderDashboard'), href: routeConfig.builder.dashboard, privilege: 1016 },
            { label: t('SidebarV2.SubMenu.BuilderReport'), href: routeConfig.builder.report },
            { label: t('SidebarV2.SubMenu.BuilderApp'), href: routeConfig.builder.app },
          ]
        },
        { 
          type: "parent", 
          label: t('SidebarV2.Menu.CashAdvance'), 
          icon: 'payments',  
          rootHref: routeConfig.cashAdvance.root,
          subMenus: [
            { label: t('SidebarV2.SubMenu.CashAdvance'), href: routeConfig.cashAdvance.master, privilege: 1014 },
            { label: t('SidebarV2.SubMenu.CashAdvanceTransaction'), href: routeConfig.cashAdvance.transaction, privilege: 1015 },
          ]
        },
      ]
    }
  ]

  function grantedMenus(userTeamId: number): GroupMenuProps[] {
    const grantedIds = new Set<number>()

    // only collect top-level privileges, ignore menu_action
    menuPrivilegeState.privilege.forEach(p => {
      grantedIds.add(p.id)
    })

    const validate = (privilege?: number, teamIds?: number[]) => {
      if (privilege !== undefined && !grantedIds.has(privilege)) {
        return false
      }
      if (teamIds !== undefined && !teamIds.includes(userTeamId)) {
        return false
      }
      return true
    }

    const filterMenu = (menu: MenuProps): MenuProps | null => {
      if (menu.type === "standalone") {
        return validate(menu.privilege, menu.teamIds) ? menu : null
      }

      if (menu.type === "parent") {
        const filteredSubMenus = menu.subMenus.filter(
          sub => validate(sub.privilege, sub.teamIds)
        )

        if (filteredSubMenus.length > 0) {
          return { ...menu, subMenus: filteredSubMenus }
        }
        return null
      }

      return null
    }

    return groupMenus
      .map(group => {
        const filteredMenus = group.menus
          .map(filterMenu)
          .filter((m): m is MenuProps => m !== null)

        if (filteredMenus.length > 0) {
          return { ...group, menus: filteredMenus }
        }
        return null
      })
      .filter((g): g is GroupMenuProps => g !== null)
  }

  function searchMenus(groupMenus: GroupMenuProps[], query: string): GroupMenuProps[] {
    if (!query.trim()) return groupMenus

    const lowerQuery = query.toLowerCase()

    return groupMenus
      .map(group => {
        const filteredMenus = group.menus
          .map(menu => {
            if (menu.type === "standalone") {
              return menu.label.toLowerCase().includes(lowerQuery) ? menu : null
            }

            if (menu.type === "parent") {
              const filteredSubMenus = menu.subMenus.filter(sub =>
                sub.label.toLowerCase().includes(lowerQuery)
              )

              if (
                menu.label.toLowerCase().includes(lowerQuery) ||
                filteredSubMenus.length > 0
              ) {
                return { ...menu, subMenus: filteredSubMenus }
              }
            }

            return null
          })
          .filter(Boolean) as MenuProps[]

        return filteredMenus.length > 0 ? { ...group, menus: filteredMenus } : null
      })
      .filter(Boolean) as GroupMenuProps[]
  }

  return { 
    textColor,
    backgroundColor,
    searchBarBackgroundColor,
    betaMenu,
    groupMenus, 
    grantedMenus,
    searchMenus, 
  }
}
